import { Box, Table, TableBody, TableCell, TableHead, TableRow, useTheme } from "@mui/material"
import { PINNED_Z_INDEX, pinnedCellSx } from "lib/@components/managed-columns/pinned-cell-styles"
import { useDarkMode } from "lib/use-dark-mode"
import { useMemo } from "react"
import {
    getRolloutAssetFields,
    isMobiliserAdvancedMappingEnabled,
} from "routes/asset-register/plugins/asset-mapping/rollout-asset-fields"
import { Repeat } from "lib/@components/repeat"
import { TextOnlyFieldHeader } from "routes/asset-register/plugins/regime-display/text-only-field-header"
import { Feature } from "lib/@components/feature"
import { Else, If } from "lib/@components/switch"
import { RequestPageRepeat } from "lib/@components/request-page-repeat"
import { NoResults } from "lib/@components/no-results/no-results"
import { AssetEntry } from "routes/asset-register/asset-entry"
import { Bound } from "lib/@components/binding/Bound"

export function AssetTable({ assets, pageSize, refreshKey = "none", register, setSkip, total, canEdit = true }) {
    const theme = useTheme()
    const dark = useDarkMode()
    const upgradesEnabled = isMobiliserAdvancedMappingEnabled()
    const fields = useMemo(getFields, [])

    return (
        <Bound displayFields={fields}>
            <Box
                sx={{
                    width: 1,
                    overflow: "hidden",
                    border: "1px solid",
                    borderColor: "divider",
                    borderRadius: "8px",
                }}
            >
                <Box
                    sx={{
                        overflowX: "auto",
                        "& .MuiTableCell-root": {
                            whiteSpace: "nowrap",
                            py: 1.5,
                        },
                        "& .MuiTableRow-root": {
                            "&": {
                                borderBottom: "1px solid " + theme.palette.divider,
                            },
                            "&:last-of-type": {
                                borderBottom: "none",
                            },
                        },
                    }}
                >
                    <Table size="small">
                        <TableHead>
                            <TableRow
                                sx={{
                                    "& th": {
                                        backdropFilter: "blur(5px)",
                                        position: "sticky",
                                        top: 0,
                                    },
                                    borderRadius: 1,
                                    bgcolor: dark ? "background.paper" : "grey.100",
                                }}
                                data-cy="assets-list-header-row"
                            >
                                <TableCell sx={pinnedCellSx(PINNED_Z_INDEX.header, theme, {})}>Tag</TableCell>
                                <TableCell>Description</TableCell>
                                <TableCell>
                                    <If value={upgradesEnabled}>
                                        Imported Asset Type
                                        <Else>Type</Else>
                                    </If>
                                </TableCell>
                                <Repeat list={fields} item={<TextOnlyFieldHeader />} />
                                <Feature feature="edit-assets">
                                    <If value={canEdit}>
                                        <TableCell
                                            sx={{
                                                position: "sticky",
                                                right: 0,
                                                top: 0,
                                                zIndex: PINNED_Z_INDEX.header,
                                            }}
                                        />
                                    </If>
                                </Feature>
                            </TableRow>
                        </TableHead>
                        <RequestPageRepeat
                            portal
                            showTop
                            pageSize={pageSize}
                            showBottom={false}
                            fallback={
                                <TableRow>
                                    <TableCell colSpan={3}>
                                        <NoResults />
                                    </TableCell>
                                </TableRow>
                            }
                            refreshKey={refreshKey}
                            setSkip={setSkip}
                            Component={TableBody}
                            list={assets}
                            item={<AssetEntry register={register} canEdit={canEdit} />}
                            total={total}
                        />
                    </Table>
                </Box>
            </Box>
        </Bound>
    )

    function getFields() {
        return getRolloutAssetFields().filter(({ field }) => !["description", "type"].includes(field))
    }
}
